import React from "react";
import {
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Tooltip,
  Typography,
} from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import { findActiveItem, groupNav } from "../../config/navigation";
import { layout } from "../../theme/tokens";

/**
 * Desktop sidebar contents, rendered from the nav config.
 *
 * The list used to be nine flat rows whose only active cue was a muted blue
 * label. It is now grouped into labelled sections, and the active row is a
 * filled accent pill so the current location is legible at a glance.
 */
export default function SideNav({ items, collapsed, onNavigate }) {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const active = findActiveItem(items, pathname);
  const groups = groupNav(items);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        px: collapsed ? 1 : 1.5,
        py: 1.5,
        gap: 0.25,
        overflowY: "auto",
        overflowX: "hidden",
      }}
    >
      {groups.map((group, groupIndex) => (
        <Box key={group.section || groupIndex} sx={{ mb: 1.5 }}>
          {/* Collapsed rail has no room for headings; a hairline keeps the
              grouping readable instead. */}
          {group.section &&
            (collapsed ? (
              groupIndex > 0 && (
                <Box
                  sx={{
                    height: "1px",
                    bgcolor: "nav.divider",
                    mx: 1,
                    mb: 1,
                  }}
                />
              )
            ) : (
              <Typography
                variant="overline"
                sx={{
                  display: "block",
                  px: 1.5,
                  pt: groupIndex === 0 ? 0.5 : 1,
                  pb: 0.75,
                  color: "nav.section",
                  fontSize: "0.6875rem",
                  letterSpacing: "0.08em",
                }}
              >
                {group.section}
              </Typography>
            ))}

          <List
            disablePadding
            sx={{ display: "flex", flexDirection: "column", gap: 0.25 }}
          >
            {group.items.map((item) => {
              const Icon = item.icon;
              const selected = active?.to === item.to;

              const button = (
                <ListItemButton
                  selected={selected}
                  onClick={() => {
                    navigate(item.to);
                    onNavigate?.();
                  }}
                  sx={{
                    minHeight: 42,
                    px: collapsed ? 0 : 1.5,
                    justifyContent: collapsed ? "center" : "flex-start",
                    gap: collapsed ? 0 : 1.5,
                    position: "relative",
                    overflow: "hidden",
                    transition: "background-color .15s ease, color .15s ease",
                    color: selected ? "primary.contrastText" : "nav.muted",
                    // A flat accent fill is what makes the current page obvious.
                    bgcolor: selected ? "primary.main" : "transparent",
                    "&.Mui-selected, &.Mui-selected:hover": {
                      bgcolor: "primary.main",
                      color: "primary.contrastText",
                    },
                    "&:hover": {
                      bgcolor: selected ? undefined : "nav.hover",
                      color: selected ? "primary.contrastText" : "nav.fg",
                    },
                  }}
                >
                  <ListItemIcon
                    sx={{
                      justifyContent: "center",
                      color: "inherit",
                      minWidth: 0,
                    }}
                  >
                    <Icon sx={{ fontSize: 21 }} />
                  </ListItemIcon>
                  {!collapsed && (
                    <ListItemText
                      primary={item.label}
                      primaryTypographyProps={{
                        fontSize: "0.875rem",
                        fontWeight: selected ? 600 : 500,
                        noWrap: true,
                      }}
                    />
                  )}
                </ListItemButton>
              );

              return (
                <ListItem key={item.to} disablePadding sx={{ display: "block" }}>
                  {collapsed ? (
                    <Tooltip title={item.label} placement="right" arrow>
                      <Box>{button}</Box>
                    </Tooltip>
                  ) : (
                    button
                  )}
                </ListItem>
              );
            })}
          </List>
        </Box>
      ))}
    </Box>
  );
}

export { layout };
